import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Stack } from '@figma/fpl-components';
import { color } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { useNavigate } from 'react-router-dom';
import { I18N_KEYS } from '@/language/keys';
import { useGlobalStore } from '@/state/global.state';
import { useWorkflowStore } from '@/state/workflow.state';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_APP_VIEW_SEPARATION } from '@/consts/featureFlags';
import { useToolByWorkflowQuery } from '@/queries/useToolView';
import { assetUrl } from '@/utils/assetUrls';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { TOOL_PUBLISH_CLICKED_EVENT, TOOL_PUBLISH_SUCCEEDED_EVENT } from '@/consts/analytics.consts';
import { runEndpoints } from '@/api/toolRunEndpoints';
import { getToolPublishUnavailableModelsError } from '@/api/errors';
import { WorkflowCreditsDisplay } from '@/components/Recipe/FlowComponents/WorkflowRightToolbar/WorkflowCreditsDisplay';
import { ToolViewTitleBar } from '../ToolViewTitleBar';
import { ToolShareModal } from '../ToolShareModal';
import { ToolViewer } from '../ToolViewer';
import { ToolPreviewBanner } from './ToolPreviewBanner';
import type { ToolRunData } from '@/api/toolRunData.api';
import type { User } from '@/types/auth.types';
import type { Recipe } from '@/types/api/recipe';
import type { ToolMetadata } from '@/types/design-app.types';

export function ToolPreviewView({
  user,
  recipe,
  toolName,
  runData,
  onBackToWorkflow,
}: {
  user: User | null;
  recipe: Recipe;
  toolName: string;
  runData: ToolRunData;
  onBackToWorkflow: () => void;
}) {
  const { track } = useAnalytics();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateSnackbarData = useGlobalStore((state) => state.updateSnackbarData);
  const isGuest = !user;
  const isPublished = !!recipe.latestPublishedVersion;
  const hasPublishedRef = useRef(isPublished);
  const canPublish = recipe.version === recipe.latestVersion.version && user?.uid === recipe.owner;
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isToolShareOpen, setIsToolShareOpen] = useState(false);
  const { value: isToolViewSeparationEnabled } = useFeatureFlag(FF_APP_VIEW_SEPARATION);
  const { data: toolByWorkflow } = useToolByWorkflowQuery(recipe.id, isToolViewSeparationEnabled === true);
  const hasMintedToolRef = useRef(false);
  // Decided once per click in onMutate, so the payload, the analytics and the snackbar agree even if
  // the lookup refetches while the deploy is in flight.
  const isUpdateRef = useRef(false);
  // Whether a tool row exists, not whether the workflow was deployed: one deployed before tools existed
  // gets its first tool here. A lookup that cannot tell falls back to the deploy.
  const isToolUpdate = () =>
    hasMintedToolRef.current || (toolByWorkflow ? Boolean(toolByWorkflow.toolId) : hasPublishedRef.current);

  const creator = user?.uid === recipe.owner ? { displayName: user.displayName, photoURL: user.photoURL } : undefined;
  const draft = recipe.toolConfig?.metadata;
  const metadata = {
    name: toolName,
    description: draft?.description,
    thumbnail: assetUrl(draft?.thumbnail ?? undefined, recipe.resolvedAssets ?? {}),
    thumbnailKey: draft?.thumbnail?.blobstore?.id,
  } satisfies ToolMetadata;

  const publishDesignApp = useWorkflowStore((state) => state.publishDesignApp);
  const publish = useMutation({
    // An update sends only what the draft itself holds: the server writes it onto the live tool, so the
    // workflow-name fallback must not rename it, and `''` carries a cleared description or a removed
    // thumbnail. The first publish keeps the fallback, which also makes the server surface a failed
    // mint instead of swallowing it.
    mutationFn: () =>
      publishDesignApp(
        isUpdateRef.current
          ? {
              ...metadata,
              name: draft?.name?.trim() ?? '',
              thumbnail: draft?.thumbnail === null ? '' : metadata.thumbnail,
            }
          : metadata,
        recipe.id,
      ),
    onMutate: () => {
      isUpdateRef.current = isToolUpdate();
      track(
        TOOL_PUBLISH_CLICKED_EVENT,
        {
          source: 'preview',
          workflow_id: recipe.id,
          version: runData.recipeVersion,
          first_time: !isUpdateRef.current,
        },
        TrackTypeEnum.BI,
      );
    },
    onError: (e) => {
      // Names the models to replace, so it stays up long enough to read.
      const unavailableModelsMessage = getToolPublishUnavailableModelsError(e);
      updateSnackbarData({
        text:
          unavailableModelsMessage ??
          t(
            isUpdateRef.current
              ? I18N_KEYS.SHARED_DESIGN_APP.UPDATE_TOOL_FAILED
              : I18N_KEYS.SHARED_DESIGN_APP.PUBLISH_DESIGN_APP_FAILED,
          ),
        icon: null,
        severity: 'error',
        duration: unavailableModelsMessage ? 8000 : 2000,
        isOpen: true,
        action: null,
      });
    },
    onSuccess: (publishedRecipe) => {
      track(
        TOOL_PUBLISH_SUCCEEDED_EVENT,
        {
          source: 'preview',
          workflow_id: publishedRecipe.id,
          version: publishedRecipe.latestPublishedVersion?.version,
          first_time: !isUpdateRef.current,
        },
        TrackTypeEnum.BI,
      );
      updateSnackbarData({
        text: t(
          isUpdateRef.current
            ? I18N_KEYS.SHARED_DESIGN_APP.UPDATE_TOOL_SUCCESS
            : I18N_KEYS.SHARED_DESIGN_APP.PUBLISH_DESIGN_APP_SUCCESS,
        ),
        icon: null,
        severity: 'success',
        duration: 2000,
        isOpen: true,
        action: null,
      });
      navigate(`/tool/${publishedRecipe.id}`, { state: { published: true } });
    },
  });

  return (
    <Stack.Col {...stylex.props(styles.root)}>
      <ToolViewTitleBar
        name={metadata.name ?? ''}
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={() => setIsSidebarOpen((open) => !open)}
        credits={!isGuest ? <WorkflowCreditsDisplay showCreditsLabel /> : undefined}
        showShare={user?.uid === recipe.owner}
        shareDisabledTooltip={!toolByWorkflow?.toolId}
        onShare={() => setIsToolShareOpen(true)}
      />
      <ToolPreviewBanner
        onBackToWorkflow={onBackToWorkflow}
        canPublish={canPublish}
        publish={publish}
        isPublished={isPublished}
        publishedAt={recipe.latestPublishedVersion?.publishedAt}
      />
      <Stack.Col {...stylex.props(styles.viewer)}>
        <ToolViewer
          isPreviewMode
          endpoints={runEndpoints({ kind: 'recipe', recipeId: recipe.id })}
          runData={runData}
          // Preview starts fresh without displaying historical outputs.
          userData={null}
          toolName={metadata.name ?? ''}
          metadata={{ ...metadata, creator }}
          isGuest={isGuest}
          isOwner={user?.uid === recipe.owner}
          isSidebarOpen={isSidebarOpen}
          isToolViewRedesignEnabled
        />
      </Stack.Col>
      {isToolShareOpen && toolByWorkflow?.toolId && (
        <ToolShareModal toolId={toolByWorkflow.toolId} open onClose={() => setIsToolShareOpen(false)} />
      )}
    </Stack.Col>
  );
}

const styles = stylex.create({
  root: { height: '100%', backgroundColor: color.fsCanvasDefaultFill },
  viewer: { flex: 1, minHeight: 0, overflow: 'hidden' },
});
